import type { Reservation } from '../../Reservation/Types/reservationTypes';
import { PaymentMethodType } from '../Types/invoicesTypes';

type ReservationPaymentSummary = NonNullable<Reservation['paymentSummary']>;

export interface InvoiceReservationLookup {
  id: number;
  bookingId?: number;
  totalPrice: number;
  applicantPartyId?: number;
  invoiceBuyerPartyId?: number;
  fundingPartyId?: number;
  resolvedInvoiceScheme?: number | string;
  invoiceSchemeSnapshot?: number | string;
  unitPrice?: number;
  source?: number;
  participantsCount?: number;
  numberOfParticipants?: number;
  offerName?: string;
  applicantFirstName?: string;
  applicantLastName?: string;
  applicantEmail?: string;
  invoiceBuyerName?: string;
  invoiceBuyerStreet?: string;
  invoiceBuyerAddress?: string;
  invoiceBuyerNip?: string;
  invoiceBuyerCountryIso?: string;
  dateFrom?: string;
  dateTo?: string;
  participants?: unknown[];
  applicant?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    street?: string;
    address?: string;
    zipCode?: string;
    city?: string;
    nip?: string;
  };
  booking?: {
    id?: number;
    offerId?: number;
    offerName?: string;
    bookingDateFrom?: string;
    bookingDateTo?: string;
    price?: number;
  };
  paymentSummary?: ReservationPaymentSummary;
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const toInvoiceSchemeOrUndefined = (value: unknown): number | string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }

  return undefined;
};

const toStringOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const toPaymentMethodTypeOrUndefined = (value: unknown): PaymentMethodType | undefined => {
  const raw = toStringOrUndefined(value)?.toLowerCase();

  if (!raw) {
    return undefined;
  }

  if (raw === 'cash' || raw === 'gotowka' || raw === 'gotówka') {
    return PaymentMethodType.Cash;
  }

  if (raw === 'transfer' || raw === 'przelew') {
    return PaymentMethodType.Transfer;
  }

  return undefined;
};

export const mapReservationToInvoiceLookup = (
  reservation: Reservation | undefined | null
): InvoiceReservationLookup | undefined => {
  if (!reservation) {
    return undefined;
  }

  const bookingDateFrom =
    toStringOrUndefined((reservation as any).dateFrom) ||
    toStringOrUndefined((reservation as any).bookingDateFrom) ||
    toStringOrUndefined((reservation as any).booking?.bookingDateFrom) ||
    toStringOrUndefined((reservation as any).booking?.dateFrom);

  const bookingDateTo =
    toStringOrUndefined((reservation as any).dateTo) ||
    toStringOrUndefined((reservation as any).bookingDateTo) ||
    toStringOrUndefined((reservation as any).booking?.bookingDateTo) ||
    toStringOrUndefined((reservation as any).booking?.dateTo);

  return {
    id: reservation.id,
    bookingId: toNumber((reservation as any).bookingId) ?? toNumber((reservation as any).booking?.id),
    totalPrice: toNumber((reservation as any).totalPrice) ?? 0,
    applicantPartyId: toNumber((reservation as any).applicantPartyId),
    invoiceBuyerPartyId: toNumber((reservation as any).invoiceBuyerPartyId),
    fundingPartyId: toNumber((reservation as any).fundingPartyId),
    resolvedInvoiceScheme: toInvoiceSchemeOrUndefined((reservation as any).resolvedInvoiceScheme),
    invoiceSchemeSnapshot: toInvoiceSchemeOrUndefined((reservation as any).invoiceSchemeSnapshot),
    unitPrice: toNumber((reservation as any).unitPrice),
    source: toNumber((reservation as any).source),
    participantsCount: toNumber((reservation as any).participantsCount),
    numberOfParticipants: toNumber((reservation as any).numberOfParticipants),
    offerName:
      toStringOrUndefined((reservation as any).offerName) ||
      toStringOrUndefined((reservation as any).booking?.offerName),
    applicantFirstName:
      toStringOrUndefined((reservation as any).applicantFirstName) ||
      toStringOrUndefined((reservation as any).applicant?.firstName),
    applicantLastName:
      toStringOrUndefined((reservation as any).applicantLastName) ||
      toStringOrUndefined((reservation as any).applicant?.lastName),
    applicantEmail: toStringOrUndefined((reservation as any).applicantEmail),
    invoiceBuyerName: toStringOrUndefined((reservation as any).invoiceBuyerName),
    invoiceBuyerStreet: toStringOrUndefined((reservation as any).invoiceBuyerStreet),
    invoiceBuyerAddress: toStringOrUndefined((reservation as any).invoiceBuyerAddress),
    invoiceBuyerNip: toStringOrUndefined((reservation as any).invoiceBuyerNip),
    invoiceBuyerCountryIso: toStringOrUndefined((reservation as any).invoiceBuyerCountryIso),
    dateFrom: bookingDateFrom,
    dateTo: bookingDateTo,
    participants: Array.isArray((reservation as any).participants)
      ? ((reservation as any).participants as unknown[])
      : undefined,
    applicant: {
      firstName: toStringOrUndefined((reservation as any).applicant?.firstName),
      lastName: toStringOrUndefined((reservation as any).applicant?.lastName),
      companyName: toStringOrUndefined((reservation as any).applicant?.companyName),
      street: toStringOrUndefined((reservation as any).applicant?.street),
      address: toStringOrUndefined((reservation as any).applicant?.address),
      zipCode: toStringOrUndefined((reservation as any).applicant?.zipCode),
      city: toStringOrUndefined((reservation as any).applicant?.city),
      nip: toStringOrUndefined((reservation as any).applicant?.nip),
    },
    booking: {
      id: toNumber((reservation as any).booking?.id),
      offerId: toNumber((reservation as any).booking?.offerId),
      offerName: toStringOrUndefined((reservation as any).booking?.offerName),
      bookingDateFrom:
        toStringOrUndefined((reservation as any).booking?.bookingDateFrom) ||
        toStringOrUndefined((reservation as any).booking?.dateFrom) ||
        bookingDateFrom,
      bookingDateTo:
        toStringOrUndefined((reservation as any).booking?.bookingDateTo) ||
        toStringOrUndefined((reservation as any).booking?.dateTo) ||
        bookingDateTo,
      price: toNumber((reservation as any).booking?.price),
    },
    paymentSummary: (reservation as any).paymentSummary
      ? {
          importablePaidAmount:
            toNumber((reservation as any).paymentSummary?.importablePaidAmount) ?? 0,
          cashPaidAmount: toNumber((reservation as any).paymentSummary?.cashPaidAmount) ?? 0,
          nonCashPaidAmount: toNumber((reservation as any).paymentSummary?.nonCashPaidAmount) ?? 0,
          suggestedPaymentMethod: toPaymentMethodTypeOrUndefined(
            (reservation as any).paymentSummary?.suggestedPaymentMethod
          ),
          suggestedPaidAt: toStringOrUndefined((reservation as any).paymentSummary?.suggestedPaidAt),
          suggestedPaymentComment: toStringOrUndefined(
            (reservation as any).paymentSummary?.suggestedPaymentComment
          ),
          source: toStringOrUndefined((reservation as any).paymentSummary?.source),
          hasImportedPayment: Boolean((reservation as any).paymentSummary?.hasImportedPayment),
          isMixedPayment: Boolean((reservation as any).paymentSummary?.isMixedPayment),
        }
      : undefined,
  };
};
